import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CheckCheck, Trash2, X, CreditCard, RefreshCw, User, FileText, Info, ShoppingCart, Star, DollarSign, AlertCircle, Wifi, WifiOff, TrendingDown, Home, Scale, Bookmark, ExternalLink } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get WebSocket URL from backend URL
const getWsUrl = () => {
  if (!API_URL) return null;
  const url = new URL(API_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCountry();
  const [notifications, setNotifications] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [savedSearchAlerts, setSavedSearchAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [wsConnected, setWsConnected] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const token = localStorage.getItem('token');
  const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('alert_email');

  // Fetch price alerts
  const fetchPriceAlerts = async () => {
    if (!userEmail) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/compare/price-alerts`, {
        params: { user_email: userEmail }
      });
      const alerts = response.data.alerts || [];
      // Filter to show only triggered alerts (price dropped)
      const triggered = alerts.filter(a => a.is_triggered || a.last_check_status === 'triggered');
      setPriceAlerts(triggered.slice(0, 5));
    } catch (error) {
      console.error('Error fetching price alerts:', error);
    }
  };

  // Fetch saved search alerts - uses authenticated saved searches endpoint
  const fetchSavedSearchAlerts = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/saved-searches/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns {success, searches: [...], total}. Guard against unexpected shapes.
      const raw = response.data?.searches ?? response.data?.saved_searches ?? response.data;
      const searches = Array.isArray(raw) ? raw : [];
      const alertItems = searches
        .filter(s => s.new_listings_count > 0 || s.has_new_matches)
        .slice(0, 5)
        .map(s => ({
          id: s.id,
          search_name: s.name || s.search_name || 'Saved Search',
          new_listings_count: s.new_listings_count || 1,
          location: s.location || s.filters?.location || '',
          query: s.query || s.filters?.q || '',
          created_at: s.created_at
        }));
      setSavedSearchAlerts(alertItems);
    } catch (error) {
      console.error('Error fetching saved search alerts:', error);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/notifications/?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // WebSocket connection for real-time notifications
  const connectWebSocket = useCallback(() => {
    if (!userEmail) return;
    
    const wsUrl = getWsUrl();
    if (!wsUrl) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/${encodeURIComponent(userEmail)}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        // Send ping to keep alive
        ws.send('ping');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle different notification types
          if (data.type) {
            // Add to notifications list
            setNotifications(prev => [data, ...prev.slice(0, 9)]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast for important notifications
            if (['price_alert', 'wanted_match', 'order_update'].includes(data.type)) {
              toast.info(data.title, {
                description: data.message,
                duration: 5000
              });
            }
          }
        } catch (e) {
          // Not JSON - might be pong response
          if (event.data !== 'pong') {
            console.log('WS message:', event.data);
          }
        }
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket disconnected', event.code);
        setWsConnected(false);
        
        // Reconnect after 5 seconds unless intentionally closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
      wsRef.current = ws;
      
      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      
      return () => clearInterval(pingInterval);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [userEmail]);
  
  // Connect WebSocket on mount
  useEffect(() => {
    if (userEmail) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000); // Normal closure
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [userEmail, connectWebSocket]);

  // Get icon for notification type
  const getNotificationIcon = (type, category) => {
    switch (type) {
      case 'price_alert':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'wanted_match':
        return <Star className="h-4 w-4 text-purple-500" />;
      case 'order_update':
        return <ShoppingCart className="h-4 w-4 text-blue-500" />;
      case 'review':
        return <Star className="h-4 w-4 text-amber-500" />;
      case 'system':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        break;
    }
    
    switch (category) {
      case 'credits':
        return <CreditCard className="h-4 w-4 text-yellow-500" />;
      case 'regenerations':
        return <RefreshCw className="h-4 w-4 text-purple-500" />;
      case 'membership':
        return <User className="h-4 w-4 text-blue-500" />;
      case 'ads':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'rfq':
        return <FileText className="h-4 w-4 text-purple-600" />;
      case 'inquiry':
        return <FileText className="h-4 w-4 text-teal-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get background color for notification type
  const getNotificationBg = (type, read) => {
    if (read) return 'bg-gray-50';
    
    switch (type) {
      case 'credit_depleted':
      case 'regen_depleted':
      case 'membership_expired':
      case 'ad_rejected':
      case 'quote_rejected':
        return 'bg-red-50 border-l-4 border-red-500';
      case 'credit_low':
      case 'regen_low':
      case 'membership_expiring':
      case 'rfq_expiring':
        return 'bg-yellow-50 border-l-4 border-yellow-500';
      case 'membership_upgraded':
      case 'ad_posted':
      case 'ad_approved':
      case 'credit_reset':
      case 'quote_accepted':
        return 'bg-green-50 border-l-4 border-green-500';
      case 'rfq_submitted':
      case 'rfq_matched':
      case 'quote_received':
      case 'wanted_match':
        return 'bg-purple-50 border-l-4 border-purple-500';
      case 'inquiry_received':
      case 'inquiry_response':
        return 'bg-teal-50 border-l-4 border-teal-500';
      case 'price_alert':
        return 'bg-green-50 border-l-4 border-green-500';
      case 'order_update':
        return 'bg-blue-50 border-l-4 border-blue-500';
      case 'review':
        return 'bg-amber-50 border-l-4 border-amber-500';
      default:
        return 'bg-blue-50 border-l-4 border-blue-500';
    }
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Poll for updates
  useEffect(() => {
    fetchUnreadCount();
    fetchPriceAlerts();
    fetchSavedSearchAlerts();
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchPriceAlerts();
      fetchSavedSearchAlerts();
    }, 30000);
    return () => clearInterval(interval);
  }, [token, userEmail]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      fetchPriceAlerts();
      fetchSavedSearchAlerts();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!token) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {/* WebSocket connection indicator */}
        {userEmail && (
          <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-400'}`} 
                title={wsConnected ? 'Real-time connected' : 'Connecting...'} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-[420px] bg-white rounded-xl shadow-2xl border z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Notification Center</h3>
              {(unreadCount + priceAlerts.length + savedSearchAlerts.length) > 0 && (
                <Badge className="bg-red-500 text-white text-xs">
                  {unreadCount + priceAlerts.length + savedSearchAlerts.length}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Mark all read
                </Button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-gray-50 h-10 px-2">
              <TabsTrigger value="all" className="text-xs data-[state=active]:bg-white rounded-t-md">
                All
                {unreadCount > 0 && <Badge className="ml-1 h-4 px-1 text-[10px] bg-gray-200">{unreadCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="price" className="text-xs data-[state=active]:bg-white rounded-t-md">
                <TrendingDown className="h-3 w-3 mr-1" />
                Price Alerts
                {priceAlerts.length > 0 && <Badge className="ml-1 h-4 px-1 text-[10px] bg-green-100 text-green-700">{priceAlerts.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="searches" className="text-xs data-[state=active]:bg-white rounded-t-md">
                <Bookmark className="h-3 w-3 mr-1" />
                Searches
                {savedSearchAlerts.length > 0 && <Badge className="ml-1 h-4 px-1 text-[10px] bg-purple-100 text-purple-700">{savedSearchAlerts.length}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* All Notifications Tab */}
            <TabsContent value="all" className="m-0">
              <ScrollArea className="max-h-[350px]">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    Loading...
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No notifications</p>
                    <p className="text-sm">You're all caught up!</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${getNotificationBg(notification.type, notification.read)}`}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center">
                              {getNotificationIcon(notification.type, notification.category)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className={`text-sm ${notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                                  <span className="mr-1">{notification.icon}</span>
                                  {notification.title}
                                </p>
                                <p className={`text-sm mt-0.5 ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>
                                  {notification.message}
                                </p>
                              </div>
                              <button
                                onClick={(e) => deleteNotification(notification.id, e)}
                                className="p-1 hover:bg-red-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-400">
                                {timeAgo(notification.created_at)}
                              </span>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Price Alerts Tab */}
            <TabsContent value="price" className="m-0">
              <ScrollArea className="max-h-[350px]">
                {priceAlerts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <TrendingDown className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No price drops yet</p>
                    <p className="text-sm">We'll notify you when prices drop</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-4"
                      onClick={() => { setIsOpen(false); navigate('/my-alerts'); }}
                    >
                      Manage Alerts
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {priceAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className="p-4 cursor-pointer hover:bg-green-50 transition-colors bg-green-50/50 border-l-4 border-green-500"
                        onClick={() => { setIsOpen(false); navigate('/my-alerts'); }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                              <TrendingDown className="h-5 w-5 text-green-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{alert.name}</p>
                            <p className="text-sm text-green-600 font-semibold">
                              Price dropped to your target!
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Target: {formatCurrency(alert.target_price)}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {alert.tracked_properties?.length || 0} properties
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {alert.created_at ? timeAgo(alert.created_at) : 'Recently'}
                              </span>
                            </div>
                          </div>
                          <ExternalLink className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Saved Searches Tab */}
            <TabsContent value="searches" className="m-0">
              <ScrollArea className="max-h-[350px]">
                {savedSearchAlerts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Bookmark className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No new matches</p>
                    <p className="text-sm">We'll alert you for saved searches</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-4"
                      onClick={() => { setIsOpen(false); navigate('/saved-searches'); }}
                    >
                      View Saved Searches
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {savedSearchAlerts.map((alert, idx) => (
                      <div
                        key={alert.id || idx}
                        className="p-4 cursor-pointer hover:bg-purple-50 transition-colors bg-purple-50/50 border-l-4 border-purple-500"
                        onClick={() => { setIsOpen(false); navigate('/saved-searches'); }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Home className="h-5 w-5 text-purple-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {alert.new_listings_count || 1} new listing{(alert.new_listings_count || 1) > 1 ? 's' : ''} found!
                            </p>
                            <p className="text-sm text-purple-600">
                              {alert.search_name || 'Saved Search'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {alert.location || alert.query || 'All areas'}
                            </p>
                            <span className="text-xs text-gray-400">
                              {alert.created_at ? timeAgo(alert.created_at) : 'Recently'}
                            </span>
                          </div>
                          <ExternalLink className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {wsConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-green-500" />
                  <span>Real-time</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-gray-400" />
                  <span>Polling</span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-700 text-xs"
                onClick={() => { setIsOpen(false); navigate('/notification-preferences'); }}
              >
                Preferences
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-700 text-xs"
                onClick={() => { setIsOpen(false); navigate('/my-alerts'); }}
              >
                My Alerts
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
